/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts (lib)
 🗓️ Created: 2025-01-05
 🧩 Description:
    Axios API client configuration and HTTP request utilities
    for centralized API communication throughout the platform.

 🔧 Notes:
    - Configures axios instance with base URL and timeout
    - Implements request/response interceptors for authentication
    - Handles token refresh and error management
    - Provides centralized HTTP client for all API calls
    - Supports multi-tenant architecture with tenant headers
    - Implements automatic token management and refresh

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import axios from 'axios';

// Create axios instance
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Enable credentials for cookies
    config.withCredentials = true;

    // Add correlation ID for request tracking
    if (!config.headers['X-Correlation-ID']) {
      config.headers['X-Correlation-ID'] = `corr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    // Add tenant slug if available
    const tenantSlug = localStorage.getItem('tenant-slug') || 'main';
    config.headers['X-Tenant-Slug'] = tenantSlug;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error?.config;

    // Prevent processing if no request config or it's any auth endpoint
    // (login, logout, refresh — none of these should trigger a retry loop)
    const isAuthEndpoint = originalRequest?.url?.includes('/api/auth/');
    if (!originalRequest || isAuthEndpoint) {
      return Promise.reject(error);
    }

    // Handle 401 errors (token expired) - prevent infinite loops
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token (Backend should read refresh_token cookie)
        const refreshResponse = await axios.post(`${api.defaults.baseURL || ''}/api/auth/refresh`, {}, {
          withCredentials: true,
          headers: {
            'X-Tenant-Slug': localStorage.getItem('tenant-slug') || 'main'
          }
        });

        // Refresh succeeded — notify the warning modal with the real expiresAt from server
        const refreshExpiresAt: number | undefined = refreshResponse?.data?.expiresAt;
        if (refreshExpiresAt) {
          window.dispatchEvent(new CustomEvent('session:issued', { detail: { expiresAt: refreshExpiresAt } }));
        }

        // Retry original request with credentials
        originalRequest.withCredentials = true;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed — emit event so the SessionExpiredOverlay handles
        // the UX gracefully without a hard page reload.
        // Clear only non-cookie state (cookies are cleared server-side).
        localStorage.removeItem('tenant-slug');
        localStorage.removeItem('auth-storage');
        window.dispatchEvent(new CustomEvent('session:expired'));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to set auth token (Deprecated: using cookies)
export const setAuthToken = (_token: string) => {
  // api.defaults.headers.Authorization = `Bearer ${token}`; // Deprecated
};

// Helper function to set tenant slug
export const setTenantSlug = (slug: string) => {
  api.defaults.headers['X-Tenant-Slug'] = slug;
};

// Helper function to clear auth headers (Deprecated: using cookies)
export const clearAuthHeaders = () => {
  // delete api.defaults.headers.Authorization; // Deprecated
  delete api.defaults.headers['X-Tenant-Slug'];
};

// Generic API request helper
export const apiRequest = async <T = any>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const tenantSlug = localStorage.getItem('tenant-slug') || 'main';

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Tenant-Slug': tenantSlug,
    ...(options.headers || {}),
  };

  // Cookie handles auth, so no manual Authorization header needed
  // But we MUST include credentials for the cookie to be sent

  const response = await fetch(`${api.defaults.baseURL || ''}${url}`, {
    ...options,
    headers,
    credentials: 'include', // Important for cookies!
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
};

// Upload avatar function - Cross-platform compatible
export const uploadAvatar = async (file: File, userId: string, tenantSlug: string = 'main') => {

  // Validate file before upload
  if (!file || file.size === 0) {
    throw new Error('No file provided or file is empty');
  }

  if (!userId) {
    throw new Error('User ID is required for avatar upload');
  }

  const formData = new FormData();
  formData.append('file', file);

  // Cross-platform compatible URL construction
  const baseURL = api.defaults.baseURL || '';
  const url = `${baseURL}/api/users/upload/avatar`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Tenant-Slug': tenantSlug,
        'X-User-Id': userId
        // Content-Type is automatically set by browser for FormData with boundary
      },
      credentials: 'include' // Important for cookies!
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If response is not JSON, try text
        try {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        } catch {
          // Use default error message
        }
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Unknown error occurred during avatar upload');
    }
  }
};


export default api;

// ── Global native fetch interceptor ─────────────────────────────────────────
// Dashboard widgets and some modules use native fetch() instead of Axios.
// This interceptor catches 401 responses from those calls and dispatches
// the same 'session:expired' event, stopping the console 401 flood.
// Must run after Axios is configured so cookie-based Axios calls are unaffected.
// ─────────────────────────────────────────────────────────────────────────────
(function installFetchInterceptor() {
  const _sessionExpiredErrors = new Set([
    'INVALID_TOKEN', 'NO_SESSION', 'MISSING_TOKEN',
    'INVALID_TOKEN_TYPE', 'NO_REFRESH_TOKEN'
  ]);
  let _sessionExpiredDispatched = false;

  const _originalFetch = window.fetch.bind(window);

  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const response = await _originalFetch(...args);

    if (response.status === 401 && !_sessionExpiredDispatched) {
      try {
        const cloned = response.clone();
        const body = await cloned.json().catch(() => ({}));
        if (body?.error && _sessionExpiredErrors.has(body.error)) {
          _sessionExpiredDispatched = true;
          // Small delay so in-flight Axios refresh has a chance to run first
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('session:expired'));
          }, 300);
        }
      } catch {
        // Ignore parse errors — don't prevent response from being consumed
      }
    }

    return response;
  };
})();

